import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ensureIdentity } from './cookies';

// Audience ingest is hosted directly on main-service (cross-product
// module), so we hit `<main>/audience/track` — NOT the per-product
// public-gateway proxy. main-service handles per-app DB routing via the
// `:app` URL param baked into VITE_MAIN_SERVICE_URL (e.g. `/v1/mixgenie`).
const mainServiceURL = import.meta.env.VITE_MAIN_SERVICE_URL || '';
const TRACK_URL = `${mainServiceURL}/audience/track`;

// Don't track admin pages, internal previews, or any path the catalog
// considers "behind a login". Add new entries here if we add an admin UI.
const SKIP_PATH_PREFIXES = ['/admin', '/login', '/register', '/reset-password'];

type Utm = {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
};

function readUtm(search: string): Utm | undefined {
  if (!search) return undefined;
  const params = new URLSearchParams(search);
  const utm: Utm = {
    source: params.get('utm_source') || params.get('gclid') ? (params.get('utm_source') || 'google') : undefined,
    medium: params.get('utm_medium') || (params.get('gclid') ? 'cpc' : undefined),
    campaign: params.get('utm_campaign') || undefined,
    term: params.get('utm_term') || undefined,
    content: params.get('utm_content') || undefined,
  };
  return utm.source || utm.medium || utm.campaign ? utm : undefined;
}

function shouldSkip(pathname: string): boolean {
  return SKIP_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Parse song detail URLs into the metadata fields the backend stores on
// PageView rows (sparse-indexed; lets "top songs by views" be cheap).
// URL shapes covered: /<artist>/<song>, /en/<artist>/<song>, /pt/<artist>/<song>.
function parseSongMeta(pathname: string): { artistSlug: string; songSlug: string } | undefined {
  const match = pathname.match(/^\/(?:(en|pt)\/)?([^/]+)\/([^/]+)\/?$/);
  if (!match) return undefined;
  const [, , artistSlug, songSlug] = match;
  // Avoid misclassifying /artistas/<letter> or other known non-song two-segment routes.
  if (artistSlug === 'artistas' || artistSlug === 'search') return undefined;
  return { artistSlug, songSlug };
}

interface BeaconPayload {
  visitorId: string;
  sessionId: string;
  path: string;
  host: string;
  referrer?: string;
  isSessionStart: boolean;
  utm?: Utm;
  song?: { artistSlug: string; songSlug: string };
}

function sendBeacon(payload: BeaconPayload) {
  // Wire format: x-www-form-urlencoded with the JSON payload in a single
  // `p` field. This is the only NestJS-parsed content-type that's also
  // CORS-safe for sendBeacon (application/json triggers preflight which
  // sendBeacon can't make, so beacons silently fail with JSON body).
  const form = new URLSearchParams();
  form.append('p', JSON.stringify(payload));
  const body = form.toString();
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/x-www-form-urlencoded' });
      const ok = navigator.sendBeacon(TRACK_URL, blob);
      if (ok) return;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    void fetch(TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
      keepalive: true,
      credentials: 'omit',
    });
  } catch {
    /* best-effort; analytics never throws */
  }
}

/**
 * Mount once at the App root. Subscribes to react-router location changes
 * and fires one pageview per *committed* URL. Deduplicates rapid identical
 * URLs (Strict Mode double-render in dev, route hooks that update searches
 * in a microtask, etc).
 *
 * The very first pageview of a session carries the document.referrer +
 * UTM params; subsequent pageviews omit both since the backend already
 * has them on the Session row.
 */
export function AudienceTracker() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = location.pathname + (location.search || '');
    if (shouldSkip(location.pathname)) return;
    if (lastPath.current === path) return;
    lastPath.current = path;

    const { visitorId, sessionId, isSessionStart } = ensureIdentity();
    const song = parseSongMeta(location.pathname);
    const payload: BeaconPayload = {
      visitorId,
      sessionId,
      path,
      host: window.location.host,
      isSessionStart,
    };
    if (isSessionStart) {
      const referrer = document.referrer || undefined;
      if (referrer) payload.referrer = referrer;
      const utm = readUtm(location.search);
      if (utm) payload.utm = utm;
    }
    if (song) payload.song = song;
    sendBeacon(payload);
  }, [location.pathname, location.search]);

  return null;
}
